/**
 * audio-resample.js — window.AudioResample: the sample-rate converter for audio
 * that goes INTO A FILE (delivery formats in /music-mastering, the output rate
 * of /equalizer). One converter for the site, so «48 kHz» means the same thing
 * in every tool that offers it.
 *
 *   await AudioResample.resample(bufferLike, outRate, { onProgress, signal })
 *     → AudioBuffer (or an AudioBuffer-shaped shim where the constructor is
 *       missing). Resolves to the INPUT untouched when the rates already match.
 *   AudioResample.spec(inRate, outRate) → { passHz, stopHz, attenDb, taps, L, M }
 *   AudioResample.engine() → 'wasm-simd' | 'js'
 *
 * Why not the browser: an AudioBufferSourceNode in an OfflineAudioContext of a
 * different rate interpolates LINEARLY in Chromium (measured identical to a
 * five-line linear interpolator, 2026-06-04) — aliasing and a dull top.
 *
 * Why this design (measured 2026-10-09, tests/js/audio-resample.test.mjs):
 * rational L/M polyphase with a Kaiser-windowed sinc, passband flat to 0.907 of
 * the lower Nyquist (20.0 kHz at 44.1 kHz, ±0.01 dB) and the stopband starting
 * AT that Nyquist with 120 dB — nothing above it folds back or images in. On
 * a real 197 s track the in-band residual against an ideal (FFT) interpolation
 * is −137 dB — FFmpeg's resampler at filter_size 256 gives −120 dB. The
 * converter this replaces (mastering-resample.js) placed the transition below
 * the passband: −1.3 dB at 19 kHz, −8 dB at 20 kHz, −26 dB at 21 kHz, i.e. every
 * delivery-rate export lost its top octave edge.
 *
 * Speed: ~170 taps per output for 44.1→48. The kernel runs as 357 bytes of
 * WebAssembly SIMD (source: resources/wasm/audio-resample.wat) — a 190 s
 * stereo track converts in ~0.4 s, against ~8 s for the same arithmetic in
 * plain JS, which stays as the fallback for engines without SIMD or where
 * WebAssembly cannot compile. Both kernels give the same samples (±2e-7).
 *
 * Work runs in time slices with a real macrotask between them (__yieldTick,
 * which keeps going in a hidden tab), and calls are SERIALISED: the WebAssembly
 * memory holds one job's table and buffers at a time.
 */
(function () {
  'use strict';

  var PASS = 0.907;        // passband edge, share of the lower Nyquist
  var ATTEN = 120;         // stopband attenuation, dB
  var CHUNK = 1 << 16;     // outputs per kernel call
  var SLICE_MS = 24;       // main-thread budget between yields
  var MAX_L = 4096;        // phase-table rows; standard rate pairs need ≤ 640

  /* WASM:begin — generated by scripts/build-audio-resample-wasm.mjs, do not edit */
  var WASM_B64 = 'AGFzbQEAAAABEAJgAXsBfWAHf39/f39/fwADAwIAAQUDAQABBgsCfwFBAAt/AUEACwcZBAZtZW1vcnkCAAFwAwABaQMBBHJ1bjEAAQqWAgIZACAA/R8AIAD9HwGSIAD9HwIgAP0fA5KSC/kBAgd/AnsjACEMIwEhDSABQQJ0IQsCQANAIAcgBk8NASAAIAwgC2xqIQkgBCANQQJ0aiEK/QwAAAAAAAAAAAAAAAAAAAAAIQ79DAAAAAAAAAAAAAAAAAAAAAAhD0EAIQgCQANAIAggC08NASAOIAkgCGr9AAQAIAogCGr9AAQA/eYB/eQBIQ4gDyAJIAhq/QAEECAKIAhq/QAEEP3mAf3kASEPIAhBIGohCAwACwsgBSAHQQJ0aiAOIA/95AEQADgCACAMIANqIQwCQANAIAwgAkkNASAMIAJrIQwgDUEBaiENDAALCyAHQQFqIQcMAAsLIAwkACANJAEL';
  /* WASM:end */

  function gcd(a, b) { while (b) { var t = a % b; a = b; b = t; } return a; }

  // Zeroth-order modified Bessel function of the first kind (Kaiser window).
  function besselI0(x) {
    var s = 1, t = 1, h = x / 2;
    for (var k = 1; k < 80; k++) { t *= (h / k) * (h / k); s += t; if (t < s * 1e-17) break; }
    return s;
  }

  /* The prototype runs at the upsampled rate inRate·L. Each of the L phases
     keeps K taps, stored REVERSED (oldest first) so the dot product walks the
     input forward, and K is padded to a multiple of 8 with zeros at the start
     of a row for the two-v128 loop of the SIMD kernel. */
  var _designs = {};
  function design(inRate, outRate) {
    var key = inRate + ':' + outRate;
    if (_designs[key]) return _designs[key];
    var g = gcd(inRate, outRate), L = outRate / g, M = inRate / g;
    if (L > MAX_L) throw new RangeError('AudioResample: rate pair ' + inRate + '→' + outRate + ' needs ' + L + ' phases');
    var nyq = Math.min(inRate, outRate) / 2;
    var passHz = PASS * nyq, stopHz = nyq;
    var Fup = inRate * L;
    var dw = 2 * Math.PI * (stopHz - passHz) / Fup;
    var N0 = Math.ceil((ATTEN - 7.95) / (2.285 * dw)) + 1;
    var K = Math.ceil(N0 / L); K = (K + 7) & ~7;
    // ODD length, so the group delay D is a whole sample of the upsampled
    // rate. With N = K·L (even) D was a half-integer: a 0.07 µs delay at
    // 44.1→48 that no ear hears but a null test does — −58 dB of in-band
    // residual against an ideal interpolation, −137 dB without it (measured
    // 2026-10-09 on a real track). The dropped tap is the outermost one.
    var N = K * L - 1;
    var beta = 0.1102 * (ATTEN - 8.7);
    var fc = (passHz + stopHz) / 2 / Fup;          // cycles per sample at Fup
    var D = (N - 1) / 2, i0b = besselI0(beta);
    var h = new Float64Array(N), sum = 0;
    for (var j = 0; j < N; j++) {
      var t = j - D;
      var s = t === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * t) / (Math.PI * t);
      var r = 2 * j / (N - 1) - 1;
      h[j] = s * besselI0(beta * Math.sqrt(Math.max(0, 1 - r * r))) / i0b;
      sum += h[j];
    }
    var scale = L / sum;                              // unity DC gain after ×L
    var H = new Float32Array(L * K);
    for (var p = 0; p < L; p++) {
      for (var k = 0; k < K; k++) { var at = p + k * L; H[p * K + (K - 1 - k)] = at < N ? h[at] * scale : 0; }
    }
    // Output n sits at upsampled index n·M + D: the group delay is taken out,
    // so sample 0 of the output lines up with sample 0 of the input.
    var d = { L: L, M: M, K: K, H: H, start: D, passHz: passHz, stopHz: stopHz };
    _designs[key] = d;
    return d;
  }

  var _wasm;                                          // undefined = not tried, null = unavailable
  function wasmKernel() {
    if (_wasm !== undefined) return _wasm;
    _wasm = null;
    try {
      if (typeof WebAssembly === 'undefined' || typeof atob !== 'function') return null;
      var bin = atob(WASM_B64), bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      // validate() is false where SIMD is unknown; the Module constructor
      // throws where the page's CSP forbids compiling — both mean «use JS».
      if (!WebAssembly.validate(bytes)) return null;
      _wasm = new WebAssembly.Instance(new WebAssembly.Module(bytes), {}).exports;
    } catch (_) { _wasm = null; }
    return _wasm;
  }

  /* Plain-JS twin of run1 in resources/wasm/audio-resample.wat: same table,
     same walk, same slice coordinates. */
  function jsRun1(H, K, L, M, X, Y, yOff, n, st) {
    var p = st.p, ii = st.i;
    for (var o = 0; o < n; o++) {
      var hb = p * K, a0 = 0, a1 = 0, a2 = 0, a3 = 0;
      for (var j = 0; j < K; j += 4) {
        a0 += H[hb + j] * X[ii + j];
        a1 += H[hb + j + 1] * X[ii + j + 1];
        a2 += H[hb + j + 2] * X[ii + j + 2];
        a3 += H[hb + j + 3] * X[ii + j + 3];
      }
      Y[yOff + o] = (a0 + a1) + (a2 + a3);
      p += M;
      while (p >= L) { p -= L; ii++; }
    }
    st.p = p; st.i = ii;
  }

  function makeOutput(nCh, len, rate) {
    if (len > 0 && typeof AudioBuffer === 'function') {
      try { return new AudioBuffer({ numberOfChannels: nCh, length: len, sampleRate: rate }); } catch (_) {}
    }
    var chans = [];
    for (var c = 0; c < nCh; c++) chans.push(new Float32Array(len));
    return {
      numberOfChannels: nCh, sampleRate: rate, length: len, duration: len / rate,
      getChannelData: function (c) { return chans[c]; }
    };
  }

  function aborted() { var e = new Error('aborted'); e.name = 'AbortError'; return e; }
  function yieldTask() {
    if (typeof window !== 'undefined' && typeof window.__yieldTick === 'function') return window.__yieldTick(0);
    return new Promise(function (r) { setTimeout(r, 0); });
  }
  function now() { return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now(); }

  async function convert(buf, inRate, outRate, opts) {
    var d = design(inRate, outRate), L = d.L, M = d.M, K = d.K, H = d.H;
    var nCh = buf.numberOfChannels, inLen = buf.length;
    var outLen = Math.ceil(inLen * L / M);
    var out = makeOutput(nCh, outLen, outRate);
    if (!outLen) return out;
    var ins = [], outs = [];
    for (var c = 0; c < nCh; c++) { ins.push(buf.getChannelData(c)); outs.push(out.getChannelData(c)); }

    var maxIn = Math.ceil(CHUNK * M / L) + K + 4;
    var w = wasmKernel(), X, Y, xOff = 0, yOff = 0;
    if (w) {
      var hBytes = H.length * 4;
      xOff = hBytes; yOff = hBytes + maxIn * 4;
      var need = yOff + CHUNK * 4;
      var have = w.memory.buffer.byteLength;
      if (have < need) w.memory.grow(Math.ceil((need - have) / 65536));
      new Float32Array(w.memory.buffer, 0, H.length).set(H);
      X = new Float32Array(w.memory.buffer, xOff, maxIn);
      Y = new Float32Array(w.memory.buffer, yOff, CHUNK);
    } else {
      X = new Float32Array(maxIn);
    }

    var m0 = d.start, p = m0 % L, i0 = (m0 - p) / L;  // phase and newest input of output 0
    var onProgress = opts.onProgress, signal = opts.signal;
    var n0 = 0, st = { p: 0, i: 0 };
    while (n0 < outLen) {
      if (signal && signal.aborted) throw aborted();
      var t0 = now();
      do {
        var n = Math.min(CHUNK, outLen - n0);
        var s = i0 - K + 1;                           // slice start: first tap of output n0
        var e = i0 + Math.ceil(n * M / L) + 1;        // newest index the last output can reach
        var a = Math.max(0, s), b = Math.min(inLen, e + 1);
        for (var ch = 0; ch < nCh; ch++) {
          X.fill(0);
          if (b > a) X.set(ins[ch].subarray(a, b), a - s);
          if (w) {
            w.p.value = p; w.i.value = 0;
            w.run1(0, K, L, M, xOff, yOff, n);
            outs[ch].set(n === CHUNK ? Y : Y.subarray(0, n), n0);
            st.p = w.p.value; st.i = w.i.value;
          } else {
            st.p = p; st.i = 0;
            jsRun1(H, K, L, M, X, outs[ch], n0, n, st);
          }
        }
        p = st.p; i0 = s + st.i + K - 1;
        n0 += n;
      } while (n0 < outLen && now() - t0 < SLICE_MS);
      if (onProgress) { try { onProgress(n0 / outLen); } catch (_) {} }
      if (n0 < outLen) await yieldTask();
    }
    return out;
  }

  var _tail = Promise.resolve();
  function resample(buf, outRate, opts) {
    opts = opts || {};
    var inRate = Math.round(buf.sampleRate);
    outRate = Math.round(outRate);
    if (!outRate || outRate === inRate) return Promise.resolve(buf);
    var job = _tail.then(function () { return convert(buf, inRate, outRate, opts); });
    _tail = job.then(function () {}, function () {});
    return job;
  }

  function spec(inRate, outRate) {
    var d = design(Math.round(inRate), Math.round(outRate));
    return { passHz: d.passHz, stopHz: d.stopHz, attenDb: ATTEN, taps: d.K, L: d.L, M: d.M };
  }

  window.AudioResample = {
    resample: resample,
    spec: spec,
    engine: function () { return wasmKernel() ? 'wasm-simd' : 'js'; }
  };
})();
